import { AutoScrollActivator, DndContext, DragEndEvent, MouseSensor, TouchSensor, TraversalOrder, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { ICalendarHeader, ICalendarItemData, ICalendarSchema, ICalendarViewRowItem, ICalendarViewRowSpanInfoItem } from 'Calendar/types';
import { STAFF_CALENDAR_PAGE_SIZE, STAFF_CALENDAR_PAGE_ZOOM_SIZE } from 'constants/calendar';
import set from 'lodash/set';
import moment from 'utils/moment';
import { memo, useCallback, useMemo } from 'react';
import aptActions from 'services/actions/apt';
import { aptUIActions } from 'services/reducers/apt';
import { calendarSelectors } from 'services/selectors/apt';
import shopSelectors from 'services/selectors/shop';
import { ICalendarDropData } from 'services/types/apt';
import { useAppDispatch } from 'store/hooks';
import { momentTimezone } from 'utils/time';
import { verifyPinCode } from 'widgets/ModalVerifyAdminPin';
import CalendarStyled from '../styles';
import { DraggableOverlay } from './DnD/DraggableOverlay';
import { DroppableStyled } from './DnD/Droppable';
import RealtimeLine from './RealtimeLine';
import CalendarViewRow from './Row';
const DATE_VALUE_FORMAT = 'MM-DD-YYYY HH:mm:ss';
const dateRex = /(^0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-(\d{4}$)/;
const autoscrollSetting = {
  activator: AutoScrollActivator.Pointer,
  threshold: { x: 0.3, y: 0.18 },
  acceleration: 100,
  interval: 2,
  order: TraversalOrder.TreeOrder,
  canScroll: (el: any) => el?.id === 'drag-scroll-container',
};

type IProps = {
  rows: ICalendarViewRowItem[];
  headers: ICalendarHeader<any>[];
  rowsSpanInfo: ICalendarViewRowSpanInfoItem[];
  distanceTimeline: ICalendarSchema['distanceTimeline'];
  fullScreen: boolean;
  enablePaging: boolean;
};
const CalendarViewBody = ({ rows = [], rowsSpanInfo, distanceTimeline, headers, fullScreen = false, enablePaging = false }: IProps) => {
  const dispatch = useAppDispatch();
  const activeTimeRow = calendarSelectors.activeTimeRow();
  const mouseSensor = useSensor(MouseSensor, {
    // Require the mouse to move by 10 pixels before activating
    activationConstraint: {
      distance: 10,
    },
  });
  const touchSensor = useSensor(TouchSensor, {
    // Press delay of 250ms, with tolerance of 5px of movement
    activationConstraint: {
      delay: 500,
      tolerance: 8,
    },
  });

  const sensors = useSensors(mouseSensor, touchSensor,);

  const scheduleBooking = shopSelectors.scheduleBooking();

  const setActiveTimeRow = useCallback((value: string) => {
    dispatch(aptUIActions.activeTimeRow(value));
  }, [dispatch]);

  const onDragEnd = useCallback((event: DragEndEvent) => {

    const appointmentItem = (event.active?.data?.current?.data) as ICalendarItemData;
    if (!appointmentItem) return;
    const [timeStr, droppable] = String(event.over?.id || '').split('/');

    let timeStart = momentTimezone(appointmentItem.startTime);
    const timeEnd = momentTimezone(appointmentItem.endTime);
    const distance = timeEnd.diff(timeStart, 'minutes');
    if (dateRex.test(droppable)) {
      timeStart = moment(droppable, 'DD-MM-YYYY');
    }

    const time = moment(timeStr, 'HH:mm');
    timeStart.set({
      hour: time.get('hour'),
      minute: time.get('minute'),
      second: 0,
    });
    const payload: ICalendarDropData = {
      id: appointmentItem.id,
      startTime: timeStart.format(DATE_VALUE_FORMAT),
      distance,
    };

    if (!dateRex.test(droppable)) {
      set(payload, 'staffId', droppable);
    }
    verifyPinCode.current?.checkAdmin((pinCode) => {
      set(payload, 'pinCode', pinCode);
      dispatch(aptActions.calendar.updateDrop.fetch(payload));
    });
  }, [dispatch]);

  const PaddingItems: any = useMemo(() => {
    if (!enablePaging) return null;
    const lengthItem = (fullScreen ? STAFF_CALENDAR_PAGE_ZOOM_SIZE : STAFF_CALENDAR_PAGE_SIZE) - (headers?.length || 0);
    if (!lengthItem || lengthItem < 0) return null;
    const arr = Array.from(Array(lengthItem).keys());
    return <>
      {arr.map((o) => (
        <CalendarStyled.TD key={o} className={'CalendarStyled_none'} >
          <DroppableStyled>
            <div className='children'
              style={{ border: '0.5px solid #F8F9FA', flex: 1, background: '#F8F9FA' }}
            >
            </div>
          </DroppableStyled>
        </CalendarStyled.TD>
      ))}
    </>;
  }, [headers, fullScreen, enablePaging]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      autoScroll={autoscrollSetting}
    >
      <CalendarStyled.TBody className='zoom-control-body' activeTimeRow={activeTimeRow}>
        {rows.map((row, index) => (
          <CalendarViewRow
            key={row.id}
            data={row}
            index={index}
            rowsSpanInfo={rowsSpanInfo}
            distance={distanceTimeline}
            scheduleBooking={scheduleBooking}
            setActiveTimeRow={setActiveTimeRow}
            paddingItems={PaddingItems}
          />
        ))}
        <RealtimeLine />
      </CalendarStyled.TBody>
      <DraggableOverlay />
    </DndContext>
  );
};

export default memo(CalendarViewBody);
